/**
 * ReportTable
 *
 * One flat row per record under a frozen header, scrolling inside its own
 * container. Deliberately plain: commit 92b1263 tore out the prebill-imitation
 * export because banner rows and merged cells did not survive a sort, a filter
 * or a pivot. The same rule applies on screen.
 */

import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";

export interface ReportColumn<T> {
	key: string;
	header: string;
	/** Right-align numerics; the default is left. */
	align?: "left" | "right";
	/** Rendered cell. Return a string for plain values. */
	cell: (row: T) => ReactNode;
	/** Optional fixed width, e.g. "8rem". */
	width?: string;
}

interface ReportTableProps<T> {
	columns: Array<ReportColumn<T>>;
	rows: T[];
	rowKey: (row: T) => string;
	emptyMessage?: string;
	/** Dim the body while a background refetch swaps the data underneath. */
	isStale?: boolean;
	/**
	 * Makes rows expandable. Returning null for a row leaves it un-expandable
	 * and hides its chevron.
	 */
	renderExpanded?: (row: T) => ReactNode;
	expandedKeys?: ReadonlySet<string>;
	onToggleRow?: (key: string) => void;
}

export default function ReportTable<T>({
	columns,
	rows,
	rowKey,
	emptyMessage = "No rows for this period.",
	isStale = false,
	renderExpanded,
	expandedKeys,
	onToggleRow,
}: ReportTableProps<T>) {
	const expandable = Boolean(renderExpanded && onToggleRow);
	if (rows.length === 0) {
		return (
			<div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
				{emptyMessage}
			</div>
		);
	}

	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full border-collapse text-sm">
				<thead className="sticky top-0 z-10 bg-muted">
					<tr>
						{expandable && <th className="w-8 px-1 py-2" aria-label="Expand" />}
						{columns.map((c) => (
							<th
								key={c.key}
								style={c.width ? { width: c.width } : undefined}
								className={`whitespace-nowrap px-3 py-2 font-medium text-muted-foreground ${
									c.align === "right" ? "text-right" : "text-left"
								}`}
							>
								{c.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody
					className={isStale ? "opacity-60 transition-opacity" : undefined}
				>
					{rows.map((row) => {
						const key = rowKey(row);
						const expandedContent = renderExpanded?.(row) ?? null;
						const canExpand = expandable && expandedContent !== null;
						const isOpen = canExpand && Boolean(expandedKeys?.has(key));
						return (
							<Fragment key={key}>
								<tr
									className={`border-t border-border hover:bg-muted/50 ${
										canExpand ? "cursor-pointer" : ""
									}`}
									onClick={canExpand ? () => onToggleRow?.(key) : undefined}
								>
									{expandable && (
										<td className="px-1 py-2 align-middle text-muted-foreground">
											{canExpand &&
												(isOpen ? (
													<ChevronDown className="h-4 w-4" />
												) : (
													<ChevronRight className="h-4 w-4" />
												))}
										</td>
									)}
									{columns.map((c) => (
										<td
											key={c.key}
											className={`px-3 py-2 ${
												c.align === "right"
													? "text-right tabular-nums"
													: "text-left"
											}`}
										>
											{c.cell(row)}
										</td>
									))}
								</tr>
								{isOpen && (
									<tr className="border-t border-border bg-muted/30">
										<td colSpan={columns.length + 1} className="px-3 py-3">
											{expandedContent}
										</td>
									</tr>
								)}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

// ─────────────────────────────────────────────
// Shared cell renderers
// ─────────────────────────────────────────────

/**
 * Hours, to one decimal.
 *
 * Renders negatives rather than hiding them — reversals issued by the billing
 * system carry negative units (commit a41d9c0).
 */
export function formatHours(value: number): string {
	return value.toFixed(1);
}

/**
 * Signed variance with a colour cue.
 *
 * The sign carries the meaning on its own, so colour is reinforcement rather
 * than the only channel.
 */
export function VarianceCell({ value }: { value: number | null }) {
	if (value === null) {
		return <span className="text-muted-foreground">—</span>;
	}
	const rounded = Math.round(value * 10) / 10;
	const tone =
		rounded > 0
			? "text-emerald-600 dark:text-emerald-400"
			: rounded < 0
				? "text-red-600 dark:text-red-400"
				: "text-muted-foreground";
	const sign = rounded > 0 ? "+" : "";
	return (
		<span className={tone}>
			{sign}
			{rounded.toFixed(1)}
		</span>
	);
}

/** Horizontal progress bar, clamped so a big overshoot cannot break the layout. */
export function PaceBar({ pct }: { pct: number | null }) {
	if (pct === null) {
		return <span className="text-muted-foreground">—</span>;
	}
	const clamped = Math.max(0, Math.min(pct, 150));
	const width = (clamped / 150) * 100;
	const tone =
		pct >= 105
			? "bg-emerald-500"
			: pct <= 95
				? "bg-red-500"
				: "bg-[var(--brand-600)]";
	return (
		<div className="flex items-center gap-2">
			<div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
				<div className={`h-full ${tone}`} style={{ width: `${width}%` }} />
			</div>
			<span className="tabular-nums text-xs text-muted-foreground">{pct}%</span>
		</div>
	);
}
